/**
 * @file CustomerRatesSection.tsx
 * @brief Customer list-rates zone — product switcher + one editor at a time.
 *
 * @details
 * Avoids stacking four product × three currency grids. Default product is
 * Meeting Intel (most confused with Enterprise). (30-07-2026)
 *
 * @author DES
 * @date 30-07-2026
 */

import { useState } from "react";
import {
  Headphones,
  Radio,
  type LucideIcon,
  PenLine,
} from "lucide-react";
import { BoardroomTable } from "@/components/icons/BoardroomTable";
import { MedicalReport } from "@/components/icons/MedicalReport";
import { GridBand } from "@/features/analytics/components/GridBlock";
import { FormSheetTabs } from "@/components/ui/FormSheetTabs";
import { BILLING_SECTIONS } from "../billingNav";
import type { SystemSettingsResponse } from "../schemas";
import { PlatformTaxCard } from "./PlatformTaxCard";
import {
  ProductRatesCard,
  type ProductRateCurrencyCode,
} from "./ProductRatesCard";

type ProductId =
  | "interpretation"
  | "realtime"
  | "meeting-intel"
  | "notes"
  | "medical";

const PRODUCTS: {
  id: ProductId;
  title: string;
  description: string;
  icon: LucideIcon;
  rateKey?: (c: ProductRateCurrencyCode) => string;
  minKey?: (c: ProductRateCurrencyCode) => string;
  speakerFeeKey?: (c: ProductRateCurrencyCode) => string;
  fixedKey?: (c: ProductRateCurrencyCode) => string;
}[] = [
  {
    id: "interpretation",
    title: "Interpretation",
    description: "Turn-by-turn interpreting. Per-minute + minimum charge.",
    icon: Headphones,
    rateKey: (c) => `RATE_PER_MINUTE_${c}_MINOR`,
    minKey: (c) => `MIN_CHARGE_${c}_MINOR`,
  },
  {
    id: "realtime",
    title: "Realtime",
    description: "Live realtime translate. Priced separately from Interpretation.",
    icon: Radio,
    rateKey: (c) => `RATE_REALTIME_PER_MINUTE_${c}_MINOR`,
    minKey: (c) => `MIN_CHARGE_REALTIME_${c}_MINOR`,
  },
  {
    id: "meeting-intel",
    title: "Meeting Intel",
    description:
      "Boardroom MI. Per minute + per-speaker fee + min charge. Enterprise pool / overage use these (or company custom).",
    icon: BoardroomTable, // DES 08-10-2026: app icon, was Sparkles
    rateKey: (c) => `RATE_BOARDROOM_PER_MINUTE_${c}_MINOR`,
    speakerFeeKey: (c) => `BOARDROOM_SPEAKER_FEE_${c}_MINOR`,
    minKey: (c) => `MIN_CHARGE_BOARDROOM_${c}_MINOR`,
  },
  {
    id: "notes",
    title: "Personal Notes",
    description: "Voice notes. Per minute + min charge (no speaker fee).",
    icon: PenLine, // DES 08-10-2026: app icon, was FileText
    rateKey: (c) => `RATE_NOTE_PER_MINUTE_${c}_MINOR`,
    minKey: (c) => `MIN_CHARGE_NOTE_${c}_MINOR`,
  },
  {
    id: "medical",
    title: "Medical Assessment",
    description:
      "Report fee, charged on every assessment. English/recorded pays it and nothing else; bilingual/interpreted pays it on top of the Interpretation per-minute rate.",
    icon: MedicalReport, // DES 08-10-2026: app icon, was FileHeart
    fixedKey: (c) => `MEDICAL_REPORT_FIXED_${c}_MINOR`,
  },
];

interface CustomerRatesSectionProps {
  data: SystemSettingsResponse | undefined;
}

export function CustomerRatesSection({ data }: CustomerRatesSectionProps) {
  const [productId, setProductId] = useState<ProductId>("meeting-intel");
  const [currency, setCurrency] = useState<ProductRateCurrencyCode>("GBP");
  const product = PRODUCTS.find((p) => p.id === productId) ?? PRODUCTS[2];
  const Icon = product.icon;

  return (
    <>
      <GridBand id={BILLING_SECTIONS.customerRates} className="bg-muted/25 py-2.5">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          What customers pay
        </p>
        <h3 className="mt-0.5 text-base font-semibold tracking-tight">
          Customer rates
        </h3>
        <p className="mt-0.5 text-sm text-muted-foreground">
          Platform <strong className="font-medium text-foreground">list rates</strong>.
          Company custom rates (Edit Company) override per tenant. Enterprise
          shared pool and overage use Meeting Intel / Notes list or that
          company’s custom MI/Notes rates.
        </p>
      </GridBand>

      <GridBand className="py-3">
        {/* The phone's pill track (DES 08-10-2026, every desktop tab row). */}
        <FormSheetTabs<ProductId>
          compact
          label="Product"
          tabs={PRODUCTS.map((p) => ({ id: p.id, label: p.title, icon: p.icon }))}
          value={productId}
          onChange={setProductId}
        />
        <div className="mt-3 flex items-start gap-2">
          <Icon className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-foreground">{product.title}</p>
            <p className="text-xs text-muted-foreground">{product.description}</p>
          </div>
        </div>
      </GridBand>

      <ProductRatesCard
        key={product.id}
        data={data}
        title={product.title}
        description={product.description}
        icon={product.icon}
        rateKey={product.rateKey}
        minKey={product.minKey}
        speakerFeeKey={product.speakerFeeKey}
        fixedKey={product.fixedKey}
        embedded
        currency={currency}
        onCurrencyChange={setCurrency}
      />

      <PlatformTaxCard data={data} />
    </>
  );
}
